<script setup lang="ts">
import { useStorage } from "@vueuse/core"
import { computed, ref } from "vue"
import { Alert, Button, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

const { t } = useTranslation()

const isAnimating = ref<boolean>(false)
const isDismissed = useStorage("in-app-browser-warning", false)

function dismissBanner() {
  isAnimating.value = true
  setTimeout(() => {
    isAnimating.value = false
    isDismissed.value = true
  }, 300)
}

const bannerClasses = computed(() => ({
  "opacity-100": !isAnimating.value,
  "opacity-0": isAnimating.value,
  "transition-opacity duration-300": true,
}))

const userAgent = navigator.userAgent

const isUsingInAppBrowser = computed(() => {
  if (!userAgent) {
    return false
  }

  // Common patterns for in-app browsers
  // 'wv' for Android WebView
  // 'FBAN', 'FBAV' for Facebook in-app browser
  // 'CrMo' for Chrome Mobile (often used in webviews)
  // 'Instagram' for Instagram in-app browser
  // 'Line' for Line in-app browser
  // 'QQBrowser' for QQ in-app browser
  // 'MicroMessenger' for WeChat in-app browser
  // 'baiduboxapp' for Baidu in-app browser
  // 'AlipayClient' for Alipay in-app browser
  // 'DingTalk' for DingTalk in-app browser

  if (/wv|CrMo|FBAN|FBAV|Instagram|Line|QQBrowser|MicroMessenger|baiduboxapp|AlipayClient|DingTalk/i.test(userAgent)) {
    return true
  }

  // More specific checks if needed
  // Example for iOS WKWebView (less reliable as 'Safari' can be present)
  // if (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent)) {
  //   return true;
  // }

  return false
})
</script>

<template>
  <Alert
    v-if="!isDismissed && isUsingInAppBrowser" compact variant="primary-solid" sharp-corners :closable="false"
    :class="bannerClasses"
  >
    <div class="flex flex-row items-center justify-between gap-4">
      <div>
        <h3 class="text-xs md:text-base font-bold">
          {{ t('in_app_browser_banner.title') }}
        </h3>
        <p class="hidden sm:inline text-xs md:text-base">
          {{ t('in_app_browser_banner.subheading') }}
        </p>
      </div>
      <Button variant="flat" type="button" class=" p-0 pr-2 text-white" @click="() => dismissBanner()">
        <Icon icon="Close" size="sm" />
      </Button>
    </div>
  </Alert>
</template>
